// Record that an asset has left the firm's possession — sold some way other
// than a product-sale charge, or disposed of (migration 0060). It moves to the
// Sold / Disposed tab on the Assets page and out of every picker. A sale's
// amount is income on the P&L ("Equipment sold") on the date it left.
//
// A drive sold to a client through a Production Media charge needs none of
// this — its time entry already marks it sold — and the API refuses it here.

import { useState, type FormEvent } from 'react';
import type { Asset } from '@stfors/shared-types';
import { Modal } from './Modal';
import { DateInput } from './DateInput';
import { MoneyInput } from './MoneyInput';
import { useRetireAsset } from '../api/queries/assets';
import { apiErrorText } from '../utils/apiErrors';
import './RetireAssetModal.css';

function todayEpoch(): number {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
}

export interface RetireAssetModalProps {
  asset: Asset;
  onClose: () => void;
}

export function RetireAssetModal({ asset, onClose }: RetireAssetModalProps): JSX.Element {
  const retire = useRetireAsset();
  const [disposition, setDisposition] = useState<'sold' | 'disposed'>('disposed');
  const [date, setDate] = useState<number | null>(todayEpoch);
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (date === null) return setError('Pick the date it left.');
    if (disposition === 'sold' && amount.trim() !== '' && !/^\d+(\.\d{1,2})?$/.test(amount.trim())) {
      return setError('Enter the amount it sold for, e.g. 350.00.');
    }
    try {
      await retire.mutateAsync({
        id: asset.id,
        disposition,
        disposed_at: date,
        ...(disposition === 'sold' && amount.trim() !== '' ? { disposal_amount: amount.trim() } : {}),
        ...(note.trim() !== '' ? { disposal_note: note.trim() } : {}),
      });
      onClose();
    } catch (err) {
      setError(apiErrorText(err, 'Could not retire the asset.'));
    }
  };

  const busy = retire.isPending;
  return (
    <Modal
      title={`Retire — ${asset.name}`}
      open
      onClose={onClose}
      busy={busy}
      footer={
        <>
          <button type="button" className="btn-charcoal-secondary" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button type="submit" form="retire-asset-form" className="btn-cyan-primary" disabled={busy}>
            {busy ? 'Saving…' : disposition === 'sold' ? 'Mark sold' : 'Mark disposed'}
          </button>
        </>
      }
    >
      <form id="retire-asset-form" className="ram-form" onSubmit={onSubmit} noValidate>
        <p className="ram-lead">
          It leaves the inventory list and every picker. You can return it to inventory later.
        </p>

        <div className="ram-choice" role="radiogroup" aria-label="What happened to it">
          {(['disposed', 'sold'] as const).map((d) => (
            <button
              key={d}
              type="button"
              role="radio"
              aria-checked={disposition === d}
              className={disposition === d ? 'ram-choice-btn is-active' : 'ram-choice-btn'}
              onClick={() => setDisposition(d)}
              disabled={busy}
            >
              {d === 'disposed' ? 'Disposed of' : 'Sold'}
            </button>
          ))}
        </div>

        <label className="ram-field">
          <span>{disposition === 'sold' ? 'Date sold *' : 'Date disposed of *'}</span>
          <DateInput value={date} onChange={setDate} disabled={busy} required />
        </label>

        {disposition === 'sold' && (
          <label className="ram-field">
            <span>Sold for</span>
            <MoneyInput value={amount} onChange={setAmount} disabled={busy} aria-label="Sold for" />
            <span className="ram-hint">Counted as income on the P&amp;L, on the date it sold.</span>
          </label>
        )}

        <label className="ram-field">
          <span>Note</span>
          <textarea
            className="form-input"
            rows={2}
            maxLength={2000}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            disabled={busy}
            placeholder={disposition === 'sold' ? 'Who bought it' : 'Recycled, wiped and shredded, lost…'}
          />
        </label>

        {error && <p className="ram-error" role="alert">{error}</p>}
      </form>
    </Modal>
  );
}
